import { useState } from "react";
import { useSegment } from "@/hooks/use-segment";
import { useReportsSceneStore } from "../../stores/use-reports-scene-store";
import { AssetRenderDropdown } from "./asset-render-dropdown";
import { CustomDropdownItem } from "@/components/ui/dropdown";
import { Checkbox } from "@/components/ui/form";
import { Visualisation } from "@/api/renderer/types/renderer.type";
import { getSceneRenderUrl } from "../../utils/get-scene-render-url";

/* Prop Types */
interface SceneRenderProps {
  projectId: string;
  studyId: string;
  sceneKey: string;
}

/*
 * SceneRender component
 * Renders the scene with the selected metric visualization
 */
export const SceneRender = ({ projectId, studyId, sceneKey }: SceneRenderProps) => {
  /* State */
  const [visualisation, setVisualisation] = useState<Visualisation>("heat");
  const [showAOI, setShowAOI] = useState<boolean>(true);
  const [showGazePath, setShowGazePath] = useState<boolean>(false);

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Store */
  const metric = useReportsSceneStore((state) => state.metric);

  /* Handlers */
  const handleVisualisationChange = (newVisualisation: Visualisation) => {
    // Segment Tracking
    // Segment Tracking
    segmentTrackEvent("Asset Report Visualisation Changed", {
      assetId: sceneKey,
      projectId,
      studyId,
      reportType: metric,
      from: visualisation,
      to: newVisualisation,
      studyType: "scene",
    });
    setVisualisation(newVisualisation);
  };

  const handleAOIChange = () => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Layer Toggled", {
      assetId: sceneKey,
      projectId,
      studyId,
      reportType: metric,
      layer: "hotspots",
      value: !showAOI,
      studyType: "scene",
    });

    setShowAOI(!showAOI);
  };

  const handleGazePathChange = () => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Layer Toggled", {
      assetId: sceneKey,
      projectId,
      studyId,
      reportType: metric,
      layer: "gazePath",
      value: !showGazePath,
      studyType: "scene",
    });

    setShowGazePath(!showGazePath);
  };

  /* Computed Values */
  const imageUrl = getSceneRenderUrl(projectId, studyId, sceneKey, {
    metric,
    visualisation,
    showAOI,
    showGazePath,
  });

  /* Render */
  return (
    <div className="grow flex flex-row bg-base-300 rounded-lg overflow-hidden max-h-full relative">
      {/* Visualisation Dropdown */}
      <AssetRenderDropdown visualisation={visualisation} handleVisualisationChange={handleVisualisationChange}>
        {/* Layer Controls */}
        <p className="px-2 text-sm">Layer</p>
        <CustomDropdownItem className="p-0 flex" onClick={handleAOIChange}>
          <div className="form-control grow px-4 py-2">
            <Checkbox checked={showAOI} size={"sm"} className="pointer-events-none" label="AOI" readOnly />
          </div>
        </CustomDropdownItem>
        <CustomDropdownItem className="p-0 flex" onClick={handleGazePathChange}>
          <div className="form-control grow px-4 py-2">
            <Checkbox checked={showGazePath} size={"sm"} className="pointer-events-none" label="Gaze path" readOnly />
          </div>
        </CustomDropdownItem>
      </AssetRenderDropdown>

      {/* Image Render */}
      <img src={imageUrl} alt={`${metric} scene report`} className="w-full h-full object-contain" />
    </div>
  );
};
